<template>
  <div>
    <div
      class="aviso-drop"
      :class="{ 'aviso-drop--activo': arrastrando }"
      role="button"
      tabindex="0"
      aria-label="Adjuntar archivos: arrastrá archivos acá o hacé click"
      @click="$refs.inputArchivos.click()"
      @keydown.enter.prevent="$refs.inputArchivos.click()"
      @keydown.space.prevent="$refs.inputArchivos.click()"
      @dragenter.prevent="arrastrando = true"
      @dragover.prevent="arrastrando = true"
      @dragleave.prevent="arrastrando = false"
      @drop.prevent="soltarArchivos"
    >
      <v-icon color="teal darken-1" large>mdi-cloud-upload-outline</v-icon>
      <p>Arrastrá archivos acá o hacé click</p>
      <small
        >Hasta {{ max }} archivos de 5 MB (imágenes, PDF, Word, Excel, TXT,
        CSV)</small
      >
      <input
        ref="inputArchivos"
        type="file"
        multiple
        hidden
        :accept="aceptar"
        @click.stop
        @change="elegirArchivos"
      />
    </div>
    <ul v-if="existentes.length || value.length" class="aviso-adjuntos">
      <li
        v-for="e in existentes"
        :key="'e' + e.key"
        class="aviso-adjuntos__item"
        :class="{ 'aviso-adjuntos__item--quitado': e.quitar }"
      >
        <img
          v-if="e.es_imagen && e.url"
          :src="e.url"
          :alt="'Vista previa de ' + e.nombre"
          class="aviso-adjuntos__thumb"
        />
        <v-icon v-else class="aviso-adjuntos__thumb" :color="colorArchivo(e)">{{
          iconoArchivo(e)
        }}</v-icon>
        <span class="aviso-adjuntos__nombre">{{ e.nombre }}</span>
        <small>{{ formatTamano(e.tamano) }}</small>
        <v-btn
          v-if="e.quitar"
          text
          x-small
          color="teal darken-1"
          class="aviso-btn"
          @click="marcar(e, false)"
          >Deshacer</v-btn
        >
        <v-btn
          v-else
          icon
          x-small
          :aria-label="'Quitar ' + e.nombre"
          @click="marcar(e, true)"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </li>
      <li v-for="a in value" :key="a.id" class="aviso-adjuntos__item">
        <img
          v-if="a.preview"
          :src="a.preview"
          :alt="'Vista previa de ' + a.file.name"
          class="aviso-adjuntos__thumb"
        />
        <v-icon
          v-else
          class="aviso-adjuntos__thumb"
          :color="colorArchivo({ nombre: a.file.name })"
          >{{ iconoArchivo({ nombre: a.file.name }) }}</v-icon
        >
        <span class="aviso-adjuntos__nombre">{{ a.file.name }}</span>
        <small>{{ formatTamano(a.file.size) }}</small>
        <v-btn
          icon
          x-small
          :aria-label="'Quitar ' + a.file.name"
          @click="quitarArchivo(a)"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </li>
    </ul>
  </div>
</template>

<script>
import {
  formatTamano,
  iconoArchivo,
  colorArchivo,
  extension,
  EXT_PERMITIDAS,
  MAX_ARCHIVOS,
  MAX_BYTES,
} from "../../utils/avisos.js";

const EXT_IMAGEN = ["png", "jpg", "jpeg", "webp", "gif"];

// Zona de arrastre + listas de adjuntos (nuevos y ya guardados).
// v-model: archivos nuevos [{ id, file, preview }]
// existentes.sync: archivos ya guardados [{ key, nombre, ..., quitar }]
export default {
  name: "ZonaAdjuntos",
  props: {
    value: { type: Array, default: () => [] },
    existentes: { type: Array, default: () => [] },
    max: { type: Number, default: MAX_ARCHIVOS },
  },
  data: () => ({
    arrastrando: false,
    aceptar: EXT_PERMITIDAS.map((e) => "." + e).join(","),
  }),
  beforeDestroy() {
    this.value.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
  },
  methods: {
    formatTamano,
    iconoArchivo,
    colorArchivo,

    // Validar y agregar a la lista; el cupo cuenta existentes no quitados + nuevos
    agregarArchivos(lista) {
      const nuevos = this.value.slice();
      const guardados = this.existentes.filter((e) => !e.quitar).length;
      for (const file of Array.from(lista || [])) {
        const ext = extension(file.name);
        if (!EXT_PERMITIDAS.includes(ext)) {
          this.$toastr.e(`"${file.name}": tipo de archivo no permitido`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          this.$toastr.e(`"${file.name}" supera el máximo de 5 MB`);
          continue;
        }
        const id = `${file.name}-${file.size}-${file.lastModified}`;
        if (nuevos.some((a) => a.id === id)) continue;
        if (guardados + nuevos.length >= this.max) {
          this.$toastr.e(`Máximo ${this.max} archivos por notificación`);
          break;
        }
        nuevos.push({
          id,
          file,
          preview: EXT_IMAGEN.includes(ext) ? URL.createObjectURL(file) : "",
        });
      }
      this.$emit("input", nuevos);
    },
    soltarArchivos(e) {
      this.arrastrando = false;
      this.agregarArchivos(e.dataTransfer && e.dataTransfer.files);
    },
    elegirArchivos(e) {
      this.agregarArchivos(e.target.files);
      e.target.value = ""; // permite volver a elegir el mismo archivo
    },
    quitarArchivo(a) {
      if (a.preview) URL.revokeObjectURL(a.preview);
      this.$emit(
        "input",
        this.value.filter((x) => x.id !== a.id)
      );
    },
    // Marca/desmarca un existente para quitar (se aplica al guardar)
    marcar(e, quitar) {
      if (!quitar) {
        const guardados = this.existentes.filter((x) => !x.quitar).length;
        if (guardados + this.value.length >= this.max) {
          this.$toastr.e(`Máximo ${this.max} archivos por notificación`);
          return;
        }
      }
      this.$emit(
        "update:existentes",
        this.existentes.map((x) => (x.key === e.key ? { ...x, quitar } : x))
      );
    },
  },
};
</script>

<style>
.aviso-drop { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 16px; margin-top: 4px; text-align: center; cursor: pointer; border: 2px dashed #a5d6a7; border-radius: 8px; background: #f6fbf6; color: #555; transition: background 0.15s, border-color 0.15s; }
.aviso-drop > * { pointer-events: none; }
.aviso-drop p { margin: 4px 0 0; font-weight: 600; font-size: 0.9rem; color: #333; }
.aviso-drop small { font-size: 0.72rem; color: #757575; }
.aviso-drop:hover, .aviso-drop:focus-visible, .aviso-drop--activo { background: #e8f5e9; border-color: #43a047; outline: none; }
.aviso-adjuntos { list-style: none; margin: 12px 0 8px; padding: 0; }
.aviso-adjuntos__item { display: flex; align-items: center; gap: 10px; padding: 6px 8px; border: 1px solid #eee; border-radius: 8px; margin-bottom: 6px; }
.aviso-adjuntos__thumb { width: 40px; height: 40px; border-radius: 6px; object-fit: cover; flex-shrink: 0; background: #f1f8f1; display: flex; align-items: center; justify-content: center; }
.aviso-adjuntos__nombre { flex: 1; min-width: 0; text-align: left; font-size: 0.85rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.aviso-adjuntos__item small { color: #757575; white-space: nowrap; }
.aviso-adjuntos__item--quitado .aviso-adjuntos__thumb, .aviso-adjuntos__item--quitado .aviso-adjuntos__nombre, .aviso-adjuntos__item--quitado small { opacity: 0.45; }
.aviso-adjuntos__item--quitado .aviso-adjuntos__nombre { text-decoration: line-through; }
</style>
